import { Tabs, type TabsItem } from "@cynthai/ui";
import { Outlet, useParams, createFileRoute } from "@tanstack/react-router";

import { useCurrentUser } from "@/core/hooks/use-current-user";
import { useTypedRouteTabs } from "@/core/hooks/use-typed-route-tabs";
import { canManageOrg, isOrgAdmin, isSuperuser } from "@/core/lib/access";
import PageContainer from "@/core/ui/layout/PageContainer";
import PageHeader from "@/core/ui/layout/PageHeader";
import { useOrganization } from "@/features/organization/hooks/use-organization";
import { m } from "@/paraglide/messages";

export const Route = createFileRoute("/_protected/organization/$orgId")({
  component: RouteComponent,
});

type OrgTabKey =
  | "overview"
  | "departments"
  | "users"
  | "invitations"
  | "training"
  | "review";

const TAB_ROUTE_MAP = {
  "/_protected/organization/$orgId/departments": "departments",
  "/_protected/organization/$orgId/users": "users",
  "/_protected/organization/$orgId/invitations": "invitations",
  "/_protected/organization/$orgId/training": "training",
  "/_protected/organization/$orgId/review": "review",
  "/_protected/organization/$orgId/": "overview",
} as const;

function RouteComponent() {
  const { orgId } = useParams({
    from: "/_protected/organization/$orgId",
  });

  const { data: organization } = useOrganization({ id: orgId });
  const currentUser = useCurrentUser();
  const selectedKey = useTypedRouteTabs<OrgTabKey>({
    tabs: TAB_ROUTE_MAP,
    fallback: "overview",
  });

  const canReviewChats = isOrgAdmin(currentUser) || isSuperuser(currentUser);

  const reviewTab: TabsItem<OrgTabKey> = {
    label: m.usage_chat_review(),
    value: "review",
    href: { to: "/organization/$orgId/review", params: { orgId } },
  };

  const trainingTab: TabsItem<OrgTabKey> = {
    label: m.training_tab(),
    value: "training",
    href: { to: "/organization/$orgId/training", params: { orgId } },
  };

  const tabs: TabsItem<OrgTabKey>[] = [
    {
      label: "Overview",
      value: "overview" as const,
      href: { to: "/organization/$orgId", params: { orgId } },
    },
    {
      label: "Departments",
      value: "departments" as const,
      href: { to: "/organization/$orgId/departments", params: { orgId } },
    },
    {
      label: "Members",
      value: "users" as const,
      href: { to: "/organization/$orgId/users", params: { orgId } },
    },
    {
      label: "Invitations",
      value: "invitations" as const,
      href: { to: "/organization/$orgId/invitations", params: { orgId } },
    },
    ...(canManageOrg(currentUser) ? [trainingTab] : []),
    ...(canReviewChats ? [reviewTab] : []),
  ];

  return (
    <>
      <PageHeader title={organization?.name ?? "Organization"} />
      <PageContainer>
        <Tabs
          items={tabs}
          selectedKey={selectedKey}
          classNames={{ panel: "flex-1 min-h-0 flex flex-col" }}
        />
        <Outlet />
      </PageContainer>
    </>
  );
}
